import type { Ref } from 'preact'
import { useEffect, useRef, useState, type Dispatch, type StateUpdater } from 'preact/hooks'
import { CalendarPlus, Check, ChevronRight, CircleCheckBig, CircleMinus, Loader2, X } from 'lucide-preact'
import { websApi, type WebViewDto } from '@/api/webs'
import { webinarRoomApi } from '@/api/webinarRoom'
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones'
import { DatePicker } from '@/components/common/DatePicker'
import { TimePicker } from '@/components/common/TimePicker'
import { TimeZoneCombobox } from '@/components/common/TimeZoneCombobox'
import { BRAND_CLASSES } from '@/constants/theme'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { retryOnStaleETag } from '@/utils/etagRetry'
import { routes } from '@/utils/routes'
import {
    RERUN_NOT_COPIED, defaultRerunDate, defaultRerunStart, earliestRerunDate, formatRerunSchedule, isKnownTimeZone, rerunCopiedItems,
    rerunHelp, rerunProblem, rerunSchedule, rerunStartDateTime, type RerunForm, type RerunSource,
} from '@/utils/webRerun'
import { BUTTON, BUTTON_GHOST, BUTTON_PRIMARY, LINE_BUTTON } from './panels/room/roomStyles'

const PANEL_ID = 'web-rerun-panel'
const MESSAGE_ID = 'web-rerun-message'
const PANEL = 'mt-4 p-5 rounded-[10px] bg-slate-50 dark:bg-slate-900/50'
const EYEBROW = 'text-[11px] leading-4 uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400'
const INVALID_FIELD = 'aria-[invalid=true]:border-red-300 aria-[invalid=true]:focus:ring-red-500'
const CLOSE_BUTTON = 'inline-flex items-center justify-center w-7 h-7 flex-none rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'

/** Why Re-run waits: a web without a start and end in its zone has no session length to carry over. */
const RERUN_NEEDS_SCHEDULE = 'Set this web’s start, end and time zone on the Details tab first.'

interface WebRerunButtonProps {
    /** The session to repeat, or null while the web has no usable schedule. */
    source: RerunSource | null
    expanded: boolean
    onToggle: () => void
    buttonRef: Ref<HTMLButtonElement>
}

/** The header's Re-run (ORB-1065) on an OrbitalX-room web, in every stage, for anyone who can edit the web. */
export function WebRerunButton({ source, expanded, onToggle, buttonRef }: WebRerunButtonProps) {
    const { canEdit } = useWorkspaceCapabilities()
    if (!canEdit) return null
    return (
        <button
            ref={buttonRef}
            type="button"
            className={LINE_BUTTON}
            aria-expanded={expanded}
            aria-controls={expanded ? PANEL_ID : undefined}
            disabled={!source}
            title={source ? undefined : RERUN_NEEDS_SCHEDULE}
            onClick={onToggle}
        >
            <CalendarPlus className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
            Re-run
        </button>
    )
}

interface WebRerunPanelProps {
    web: WebViewDto
    source: RerunSource
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    setWeb: Dispatch<StateUpdater<WebViewDto | null>>
    onClose: () => void
}

interface CreatedRerun {
    webId: string
    schedule: string
}

/**
 * Re-run, open in place under the header: the new date, start and zone, what comes over and what doesn't, then the new
 * web. The session keeps this one's length, and its zone unless another is chosen, named as it goes by on the new date.
 */
export function WebRerunPanel({ web, source, clientCompanyId, workspaceId, campaignId, setWeb, onClose }: WebRerunPanelProps) {
    const { getAbbreviation } = useTimeZones()
    const { showErrorUnless422 } = useToast()
    const [form, setForm] = useState<RerunForm>(() => ({
        date: defaultRerunDate(source, new Date()).toString(), start: defaultRerunStart(source), timeZone: source.timeZone,
    }))
    const [touched, setTouched] = useState(false)
    const [creating, setCreating] = useState(false)
    const [created, setCreated] = useState<CreatedRerun | null>(null)
    const dateRef = useRef<HTMLInputElement>(null)
    const doneRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        dateRef.current?.focus()
    }, [])
    useEffect(() => {
        if (created) doneRef.current?.focus()
    }, [created])

    const now = new Date()
    const problem = rerunProblem(source, form, now)
    const schedule = rerunSchedule(source, form)
    const shownProblem = touched ? problem : null
    const message = shownProblem?.message ?? (schedule ? rerunHelp(source, schedule, getAbbreviation) : '')

    const edit = (field: keyof RerunForm, value: string) => {
        setForm(previous => ({ ...previous, [field]: value }))
        setTouched(true)
    }

    // A re-run is an intent on this web ("run it again on that date"), so a stale ETag retries on the fresh copy.
    const create = async () => {
        setTouched(true)
        if (problem || !schedule || creating) return
        setCreating(true)
        const res = await retryOnStaleETag(
            document => webinarRoomApi.rerun(workspaceId, campaignId, web.Id, {
                ETag: document.ETag, StartDateTime: rerunStartDateTime(schedule), TimeZone: schedule.timeZone,
            }),
            web,
            async () => {
                const fresh = await websApi.getById(workspaceId, web.Id)
                if (!fresh.data) return null
                setWeb(fresh.data)
                return fresh.data
            },
        )
        setCreating(false)
        if (!res.data) {
            showErrorUnless422('Re-run not created', res, 'The re-run could not be created.')
            return
        }
        setCreated({ webId: res.data.Id, schedule: formatRerunSchedule(schedule, getAbbreviation) })
    }

    if (created) {
        return (
            <div id={PANEL_ID} className={PANEL} role="status">
                <div className="flex flex-wrap items-start gap-3">
                    <CircleCheckBig className="w-5 h-5 mt-0.5 flex-none text-green-600 dark:text-green-400" />
                    <div className="flex-1 min-w-[16rem] text-sm">
                        <p className="font-semibold text-slate-900 dark:text-white">Re-run created for {created.schedule}.</p>
                        <p className="mt-0.5 text-slate-600 dark:text-slate-300">It’s a new web in this campaign, waiting for its own sign-off.</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <a href={routes.scoped(clientCompanyId, workspaceId).web(campaignId, created.webId)} className={BUTTON}>
                            Open the new web<ChevronRight className="w-3.5 h-3.5" />
                        </a>
                        <button ref={doneRef} type="button" className={BUTTON_GHOST} onClick={onClose}>Done</button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id={PANEL_ID} className={PANEL} role="group" aria-labelledby="web-rerun-title">
            <div className="flex items-center justify-between gap-3">
                <h2 id="web-rerun-title" className="text-base font-semibold text-slate-900 dark:text-white">Run this webinar again</h2>
                <button type="button" className={CLOSE_BUTTON} aria-label="Close re-run" title="Close" onClick={onClose}>
                    <X className="w-4 h-4" />
                </button>
            </div>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">Creates a new web in this campaign for the new date.</p>
            <div className="mt-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-6">
                <div>
                    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] gap-3">
                        <DatePicker
                            ref={dateRef}
                            id="web-rerun-date"
                            label="New date"
                            required
                            value={form.date}
                            min={isKnownTimeZone(form.timeZone) ? earliestRerunDate(source, form.timeZone, now).toString() : undefined}
                            className={INVALID_FIELD}
                            aria-invalid={shownProblem?.field === 'date'}
                            aria-describedby={MESSAGE_ID}
                            onInput={(event: Event) => edit('date', (event.currentTarget as HTMLInputElement).value)}
                        />
                        <TimePicker
                            id="web-rerun-start"
                            label="Start time"
                            required
                            value={form.start}
                            step={300}
                            className={INVALID_FIELD}
                            aria-invalid={shownProblem?.field === 'start'}
                            aria-describedby={MESSAGE_ID}
                            onInput={(event: Event) => edit('start', (event.currentTarget as HTMLInputElement).value)}
                        />
                    </div>
                    <div className="mt-3">
                        <TimeZoneCombobox
                            name="web-rerun-time-zone"
                            label="Time zone"
                            required
                            value={form.timeZone}
                            invalid={shownProblem?.field === 'timeZone'}
                            describedBy={MESSAGE_ID}
                            onChange={timeZone => edit('timeZone', timeZone)}
                        />
                    </div>
                    <p id={MESSAGE_ID} className={`mt-2 text-sm ${shownProblem ? BRAND_CLASSES.INPUT.ERROR_TEXT : BRAND_CLASSES.INPUT.HELPER}`}>{message}</p>
                </div>
                <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <RerunList title="Copied" items={rerunCopiedItems(web.OnlineEvent ?? {})} copied />
                        <RerunList title="Not copied" items={RERUN_NOT_COPIED} copied={false} />
                    </div>
                    <p className="mt-2.5 text-xs text-slate-500 dark:text-slate-400">
                        Set up the landing page and emails for the new date as usual. The re-run goes through sign-off on its own.
                    </p>
                </div>
            </div>
            <div className="mt-5 flex items-center gap-2">
                <button type="button" className={BUTTON_PRIMARY} disabled={!!problem || creating} onClick={() => void create()}>
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                    Create re-run
                </button>
                <button type="button" className={BUTTON_GHOST} onClick={onClose}>Cancel</button>
            </div>
        </div>
    )
}

function RerunList({ title, items, copied }: { title: string; items: string[]; copied: boolean }) {
    const Icon = copied ? Check : CircleMinus
    return (
        <div>
            <p className={EYEBROW}>{title}</p>
            <ul className="mt-2 flex flex-col gap-1.5" aria-label={title}>
                {items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-[13px] leading-[18px] text-slate-700 dark:text-slate-200">
                        <Icon className={`w-3.5 h-3.5 mt-px flex-none ${copied ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`} />
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}
